import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FaCrown } from "react-icons/fa";

const Results = () => {
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get("challengeId");
  const [results, setResults] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const [userScore, setUserScore] = useState(null);
  const [subject, setSubject] = useState(null);
  const [topic, setTopic] = useState(null);
  const [level, setLevel] = useState(null);
  const navigate = useNavigate();
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    let intervalId;
    const fetchResults = async () => {
      if (!challengeId) {
        setError("Challenge ID not found.");
        setStatus("error");
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${baseUrl}/play/challenges/${challengeId}/result?user_id=${userId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        const data = await response.json();

        console.log("Received API response:", data);

        setMode(data.mode);
        if (data.subject) setSubject(data.subject);
        if (data.topic) setTopic(data.topic);
        if (data.level) setLevel(data.level);

        if (data.status === "complete") {
          setResults(data);
          setStatus("complete");
          clearInterval(intervalId);
        } else if (data.status === "in_progress" && data.mode === "async") {
          const isUserCreator = data.creator && data.creator.userId === userId;
          const userData = isUserCreator ? data.creator : data.opponent;
          const opponentData = isUserCreator ? data.opponent : data.creator;

          if (
            userData &&
            userData.hasSubmitted &&
            opponentData &&
            !opponentData.hasSubmitted
          ) {
            setUserScore(userData.score);
            setStatus("user-finished-waiting-opponent");
          } else if (data.status === "in_progress" && data.mode === "sync") {
            setStatus("waiting");
          } else {
            setStatus("waiting-general");
            setStatus("waiting");
          }
        } else if (data.status === "in_progress" && data.mode === "sync") {
          const isUserCreator = data.creator && data.creator.userId === userId;
          const userData = isUserCreator ? data.creator : data.opponent;
          const opponentData = isUserCreator ? data.opponent : data.creator;

          if (
            userData &&
            userData.hasSubmitted &&
            opponentData &&
            !opponentData.hasSubmitted
          ) {
            setUserScore(userData.score);
            setStatus("user-finished-waiting-opponent");
          } else {
            setStatus("waiting");
          }
        } else if (data.status === "waiting" && data.mode === "sync") {
          setStatus("waiting");
        } else {
          setError("Unexpected response status or mode.");
          setStatus("error");
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Error fetching results:", error);
        setError(error.message);
        setStatus("error");
        clearInterval(intervalId);
      }
    };

    fetchResults();
    if (status !== "complete" && status !== "error") {
      intervalId = setInterval(fetchResults, 5000);
    }

    return () => clearInterval(intervalId);
  }, [challengeId, baseUrl, userId, status]);

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading results...
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Error: {error}
      </div>
    );
  }
  if (status === "waiting" || status === "waiting-general") {
    return (
      <div className="flex justify-center items-center h-screen text-blue-600 text-xl">
        Waiting for opponent to finish the challenge...
      </div>
    );
  }

  if (status === "user-finished-waiting-opponent" && userScore !== null) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Your Score</h2>
          <p className="text-4xl font-bold text-blue-600 mb-6">{userScore}</p>
          {(subject || topic || level) && (
            <div className="mb-6 text-gray-700">
              {subject && (
                <p>
                  <span className="font-semibold">Subject:</span> {subject}
                </p>
              )}
              {topic && (
                <p>
                  <span className="font-semibold">Topic:</span> {topic}
                </p>
              )}
              {level && (
                <p>
                  <span className="font-semibold">Level:</span> {level}
                </p>
              )}
            </div>
          )}
          <p className="text-gray-600 text-xl mb-6">
            Waiting for your opponent to finish...
          </p>
          <button
            onClick={() => navigate("/practice/mygoals")}
            className="mt-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg shadow transition"
          >
            Back to Challenges
          </button>
        </div>
      </div>
    );
  }

  if (status === "complete" && results) {
    const {
      creator,
      opponent,
      winner,
      subject,
      topic,
      level,
      total_questions,
    } = results;

    const isUserCreator = creator.userId === userId;
    const userData = isUserCreator ? creator : opponent;
    const opponentData = isUserCreator ? opponent : creator;

    const isDraw = winner === "Draw";
    const userWon = winner === userData.handle;
    const place = userWon ? 1 : 2;
    const pointsBehind = Math.abs(userData.score - opponentData.score);

    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-xl">
          <div className="flex flex-col items-center mb-8">
            <div className="bg-yellow-100 rounded-full p-4 mb-4">
              <FaCrown className="text-yellow-500 text-4xl" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-gray-800">
              {isDraw
                ? "It's a Draw!"
                : `You came ${place}${place === 1 ? "st" : "nd"}!`}
            </h2>
            {!isDraw && (
              <p className="text-gray-500 text-center mb-4">
                {userWon
                  ? "Congratulations! You won the challenge!"
                  : `Awesome effort! You were just ${pointsBehind} points behind the winner.`}
                <br />
                Keep practicing to improve your skills.
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-100 rounded-xl p-4 flex flex-col items-center">
              <span className="text-2xl font-bold text-gray-700">
                {total_questions}
              </span>
              <span className="text-gray-500 text-sm mt-1">
                Total Questions
              </span>
            </div>
            <div className="bg-blue-100 border-2 border-blue-500 rounded-xl p-4 flex flex-col items-center">
              <span className="text-2xl font-bold text-blue-600">
                {userData.score}
              </span>
              <span className="text-blue-600 text-sm mt-1 font-semibold">
                Your Score
              </span>
            </div>
            <div className="bg-gray-100 rounded-xl p-4 flex flex-col items-center">
              <span className="text-2xl font-bold text-gray-700">
                {pointsBehind}
              </span>
              <span className="text-gray-500 text-sm mt-1">
                Points Difference
              </span>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-bold mb-2 text-gray-700">
              Challenge Details
            </h3>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-gray-600">
                <span className="font-semibold">Subject:</span> {subject}
              </p>
              <p className="text-gray-600">
                <span className="font-semibold">Topic:</span> {topic}
              </p>
              <p className="text-gray-600">
                <span className="font-semibold">Level:</span> {level}
              </p>
            </div>
          </div>

          <div className="flex justify-center mb-8">
            <button
              onClick={() => {
                console.log(
                  "Navigating to review with challengeId:",
                  challengeId,
                );
                navigate(`/practice/review?challengeId=${challengeId}`);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg shadow transition"
            >
              Review Quiz
            </button>
          </div>

          <div>
            <h3 className="text-lg font-bold mb-4 text-gray-700">
              Participants
            </h3>
            <div className="flex flex-col gap-3">
              {[userData, opponentData].map((p) => (
                <div
                  key={p.userId}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 ${
                    p.handle === winner
                      ? "border-yellow-400 bg-yellow-50"
                      : p.userId === userId
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-200 bg-white"
                  } ${p.userId === userId ? "ring-2 ring-blue-300" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-2xl">
                      {p.userId === userId ? "🧑" : "👤"}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-800">
                        {p.handle}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {p.score} points
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.handle === winner && (
                      <FaCrown className="text-yellow-500 text-lg" />
                    )}
                    <span
                      className={`font-bold ${p.handle === winner ? "text-yellow-600" : p.userId === userId ? "text-blue-600" : "text-gray-400"}`}
                    >
                      #{p.handle === winner ? 1 : 2}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default Results;
